import { useEffect, useState, type CSSProperties } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/app/navigation/use-navigation';
import LoadingState from '@/components/ui/LoadingState';
import EmptyState from '@/components/ui/EmptyState';
import FieldsetCard from '@/components/ui/FieldsetCard';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import { listMyKeys } from './api-keys.service';
import CreateApiKeyDrawer from './CreateApiKeyDrawer';
import KeyDashboardDrawer from './KeyDashboardDrawer';
import PostCreationModal from './PostCreationModal';
import RevokeKeyConfirm from './RevokeKeyConfirm';
import type { ApiKey, CreatedKey } from './types';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return 'Nunca';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'Nunca';
  }
}

function scopeSummary(scopes: string[]): string {
  if (scopes.length === 0) return '0 escopos';
  return `${scopes.length} escopo${scopes.length !== 1 ? 's' : ''}`;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const PAGE_STYLE: CSSProperties = {
  flex: 1,
  background: 'var(--panel)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  padding: '24px 32px',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const PAGE_HEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: '24px',
  gap: '16px',
  flexWrap: 'wrap',
};

const TABLE_STYLE: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
};

const TH_STYLE: CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const TD_STYLE: CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
  color: '#374151',
};

// ── Component ────────────────────────────────────────────────────────────────

export default function ApiKeysPage() {
  const { hasPermission, getPermissionScope } = useAuth();
  const { actions } = useNavigation();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<CreatedKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [viewingKeyId, setViewingKeyId] = useState<string | null>(null);

  const canCreate = hasPermission('API_KEY', 'CREATE');
  const canDelete = hasPermission('API_KEY', 'DELETE');
  const viewScope = getPermissionScope('API_KEY', 'view') ?? getPermissionScope('API_KEY', 'select');
  const canViewAdmin = viewScope === 'company' || viewScope === 'platform';

  const reload = async () => {
    try {
      const data = await listMyKeys();
      setKeys(data);
    } catch {
      // Errors surfaced by the service; list stays empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreated = (result: CreatedKey) => {
    setCreating(false);
    setJustCreated(result);
    reload();
  };

  const handleRevoked = () => {
    setRevoking(null);
    reload();
  };

  if (loading) {
    return <LoadingState message="Carregando chaves de API..." />;
  }

  return (
    <div style={PAGE_STYLE}>

      {/* Page header */}
      <div style={PAGE_HEADER_STYLE}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 700,
            color: '#154680',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <KeyRound size={20} color="#154680" />
            Chaves de API
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
            Gerencie as chaves de acesso para integrações externas e automações.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {canViewAdmin && (
            <button
              type="button"
              className="btn-add"
              onClick={() => actions.navigate('api-keys-admin')}
              title="Ver todas as chaves do tenant"
              style={{ background: '#fff', color: '#154680', border: '1px solid #c7d2fe' }}
            >
              <ShieldCheck size={14} />
              <span className="btn-text">Visão administrativa</span>
            </button>
          )}
          {canCreate && (
            <button
              className="btn-add plus-animated"
              onClick={() => setCreating(true)}
            >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <defs>
                <linearGradient id="apiKeyPlusG" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="21" y2="0">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#154680" />
                </linearGradient>
              </defs>
              <g className="plus-h" stroke="url(#apiKeyPlusG)"><path d="M5 12h14" /></g>
              <g className="plus-v" stroke="url(#apiKeyPlusG)"><path d="M12 5v14" /></g>
            </svg>
            <span className="btn-text">Nova chave</span>
          </button>
        )}
        </div>
      </div>

      {/* Keys table / empty state */}
      {keys.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="Nenhuma chave de API"
          description="Crie uma chave para conectar integrações externas, MCP ou automações ao Joshua."
          action={
            canCreate ? (
              <button
                className="btn-add plus-animated"
                onClick={() => setCreating(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <defs>
                    <linearGradient id="apiKeyEmptyPlusG" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="21" y2="0">
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#154680" />
                    </linearGradient>
                  </defs>
                  <g className="plus-h" stroke="url(#apiKeyEmptyPlusG)"><path d="M5 12h14" /></g>
                  <g className="plus-v" stroke="url(#apiKeyEmptyPlusG)"><path d="M12 5v14" /></g>
                </svg>
                <span className="btn-text">Criar primeira chave</span>
              </button>
            ) : undefined
          }
        />
      ) : (
        <FieldsetCard
          title="Chaves ativas"
          count={keys.length}
          shadow="md"
          style={{ overflow: 'hidden' }}
          contentStyle={{ padding: 0 }}
        >
          <table style={TABLE_STYLE}>
            <thead>
              <tr>
                <th style={TH_STYLE}>Nome</th>
                <th style={TH_STYLE}>Prefixo</th>
                <th style={TH_STYLE}>Escopos</th>
                <th style={TH_STYLE}>Último uso</th>
                <th style={TH_STYLE}>Expira em</th>
                {canDelete && (
                  <th style={{ ...TH_STYLE, textAlign: 'right' }}>Ações</th>
                )}
              </tr>
            </thead>
            <tbody>
              {keys.map((key, index) => (
                <tr
                  key={key.id}
                  onClick={() => setViewingKeyId(key.id)}
                  style={{
                    background: index % 2 === 0 ? '#fff' : '#f9fafb',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                  onMouseLeave={(e) => (e.currentTarget.style.background =
                    index % 2 === 0 ? '#fff' : '#f9fafb')}
                >
                  <td style={TD_STYLE}>
                    <span style={{ fontWeight: 600, color: '#111827' }}>{key.name}</span>
                  </td>
                  <td style={TD_STYLE}>
                    <code style={{
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      background: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      color: '#154680',
                    }}>
                      {key.prefix}&hellip;
                    </code>
                  </td>
                  <td style={TD_STYLE}>
                    <span
                      title={key.scopes.join('\n')}
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        background: '#eff6ff',
                        color: '#154680',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'default',
                      }}
                    >
                      {scopeSummary(key.scopes)}
                    </span>
                  </td>
                  <td style={{ ...TD_STYLE, color: key.last_used_at ? '#374151' : '#9ca3af' }}>
                    {formatDate(key.last_used_at)}
                  </td>
                  <td style={{ ...TD_STYLE, color: key.expires_at ? '#374151' : '#9ca3af' }}>
                    {formatDate(key.expires_at)}
                  </td>
                  {canDelete && (
                    <td style={{ ...TD_STYLE, textAlign: 'right' }}>
                      <button
                        className="btn-add gf-trash-animated"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRevoking(key);
                        }}
                        title="Revogar chave"
                        style={{ padding: '4px 8px' }}
                        aria-label={`Revogar chave ${key.name}`}
                      >
                        <TrashAnimatedIcon size={14} />
                        <span className="btn-text">Revogar</span>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </FieldsetCard>
      )}

      {/* Info card */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#0c4a6e',
        lineHeight: '1.5',
      }}>
        As chaves de API concedem acesso de longa duração em nome da sua conta. Trate-as como senhas:
        não as compartilhe em código-fonte público. Revogue chaves que não estejam mais em uso.
      </div>

      {/* Drawers / modals */}
      <CreateApiKeyDrawer
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={handleCreated}
      />

      <KeyDashboardDrawer
        isOpen={!!viewingKeyId}
        onClose={() => setViewingKeyId(null)}
        recordId={viewingKeyId}
      />

      {justCreated && (
        <PostCreationModal
          raw={justCreated.raw}
          record={justCreated.record}
          onClose={() => setJustCreated(null)}
        />
      )}

      <RevokeKeyConfirm
        item={revoking}
        onClose={() => setRevoking(null)}
        onRevoked={handleRevoked}
      />

    </div>
  );
}
